import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMapPin } = FiIcons;

const Contact = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8 }
  };

  const contactMethods = [
    {
      icon: FiPhone,
      title: 'Telephone',
      value: '(718) 380-1818',
      link: 'tel:+17183801818',
    },
    {
      icon: FiMapPin,
      title: 'Address',
      value: '80-08 Surrey Pl, Jamaica, NY 11432',
      link: 'https://www.google.com/maps/place/Ganda+Sushi/@40.726158,-73.7917959,17z/data=!3m1!4b1!4m6!3m5!1s0x89c26100ca7163ad:0x4b7fe7e097bf9f58!8m2!3d40.726154!4d-73.789221!16s%2Fg%2F1thsb8h7?entry=ttu&g_ep=EgoyMDI2MDEwNC4wIKXMDSoASAFQAw%3D%3D',
    }
  ];

  return (
    <>
      <Helmet>
        <title>Contact Us - Ganda Sushi | Reservations & Location</title>
        <meta name="description" content="Contact Ganda Sushi in Jamaica, Queens for reservations and inquiries. Experience luxury Asian fusion dining." />
      </Helmet>

      <main className="bg-luxury-cream text-luxury-dark min-h-screen">
        <section className="pt-48 pb-20 text-center">
          <motion.div {...fadeInUp} className="max-w-4xl mx-auto px-6">
            <span className="text-luxury-gold tracking-[0.4em] uppercase text-xs font-bold mb-6 block">Get in Touch</span>
            <h1 className="text-5xl md:text-8xl font-serif mb-8 text-luxury-dark">Contact Us</h1>
            {/* Darkened text color */}
            <p className="text-luxury-dark/80 text-lg max-w-2xl mx-auto leading-relaxed">
              We welcome your inquiries and look forward to hosting you soon at our sanctuary of culinary excellence.
            </p>
          </motion.div>
        </section>

        <section className="py-20 border-y border-luxury-gold/10 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {contactMethods.map((m, i) => (
                <motion.a 
                  key={i}
                  href={m.link}
                  target={m.link.startsWith('http') ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  {...fadeInUp}
                  transition={{ delay: i * 0.2 }}
                  /* Updated rounded corners to rounded-2xl */
                  className="group bg-luxury-cream/50 p-12 text-center rounded-2xl border border-luxury-gold/10 shadow-sm flex flex-col items-center justify-center transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:bg-white cursor-pointer min-h-[300px]"
                >
                  <div className="w-16 h-16 mx-auto mb-8 text-luxury-gold group-hover:scale-110 transition-transform duration-300">
                    <SafeIcon icon={m.icon} className="w-full h-full" />
                  </div>
                  <h3 className="text-xs tracking-[0.3em] uppercase font-bold text-luxury-gold mb-4 group-hover:text-luxury-dark transition-colors">{m.title}</h3>
                  <p className="text-xl font-serif text-luxury-dark break-all">{m.value}</p>
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        <section className="py-32">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-4xl md:text-6xl font-serif mb-10 text-luxury-dark">Our Hours</h2>
              <div className="space-y-6">
                {[
                  { day: 'Tuesday - Saturday', time: '11:00 AM - 9:00 PM' },
                  { day: 'Sunday', time: '12:00 PM - 9:00 PM' },
                  { day: 'Monday', time: 'Closed' }
                ].map((h, i) => (
                  <div key={i} className="flex justify-between items-center py-5 border-b border-luxury-gold/20">
                    <span className="text-luxury-dark font-medium">{h.day}</span>
                    <span className="text-luxury-gold font-serif text-lg">{h.time}</span>
                  </div>
                ))}
              </div>
              {/* Updated rounded corners to rounded-2xl and darkened text color */}
              <div className="mt-12 p-8 bg-white border border-luxury-gold/10 rounded-2xl italic text-luxury-dark/80">
                Reservations are recommended for weekend dining and larger parties.
              </div>
            </motion.div>
            
            <motion.div 
              {...fadeInUp}
              /* Updated rounded corners to rounded-2xl */
              className="h-[600px] w-full bg-white border border-luxury-gold/20 overflow-hidden relative shadow-2xl rounded-2xl"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3024.3032!2d-73.7915!3d40.7058!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c260f8f8f8f8f8%3A0x8f8f8f8f8f8f8f8f!2s80-08%20Surrey%20Pl%2C%20Jamaica%2C%20NY%2011432!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'sepia(30%) contrast(90%)' }}
                allowFullScreen=""
                loading="lazy"
                title="Ganda Sushi Location"
              />
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Contact;